/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

interface ActiveGuard {
  getParams: () => Record<string, string>;
  routePath: string;
}

/**
 * Generic service that protects a component's query params from being dropped
 * during navigation. When the component is alive but its params are missing
 * from the URL (e.g. after an ancestor/same-page sidebar navigation), they
 * are automatically restored.
 *
 * Lifecycle-based: when the component is destroyed and the returned
 * Subscription is unsubscribed, its params are no longer guarded —
 * so child-specific params naturally drop when navigating to a parent route.
 *
 * Usage:
 * ```ts
 * // ngOnInit – after the component has initialised its param state
 * this.paramGuardSub = this.queryParamGuard.protect(
 *   this.route,
 *   this.router,
 *   () => this.myAdapter.toParams(this.myState)   // or any Record<string, string>
 * );
 *
 * // ngOnDestroy
 * this.paramGuardSub.unsubscribe();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class QueryParamGuardService {
  private activeGuards = new Map<number, ActiveGuard>();
  private nextId = 0;

  /**
   * Start guarding query params owned by the calling component.
   *
   * @param route   The component's ActivatedRoute
   * @param router  The Router instance
   * @param getOwnedParams  Function returning the component's current
   *                        query-param key/value pairs that should be in the URL
   * @returns Subscription – unsubscribe in ngOnDestroy to stop guarding
   */
  protect(
    route: ActivatedRoute,
    router: Router,
    getOwnedParams: () => Record<string, string>
  ): Subscription {
    const id = this.nextId++;
    const routePath = route.snapshot.pathFromRoot
      .flatMap((s) => s.url)
      .map((u) => u.path)
      .join('/');

    this.activeGuards.set(id, { getParams: getOwnedParams, routePath });

    const sub = route.queryParams.subscribe((urlParams) => {
      const ownedParams = this.removeEmpty(getOwnedParams());

      if (Object.keys(ownedParams).length === 0) {
        return;
      }

      const hasMissing = Object.keys(ownedParams).some(
        (key) => urlParams[key] === undefined || urlParams[key] === null
      );

      if (!hasMissing) {
        return;
      }

      router.navigate([], {
        queryParams: ownedParams,
        queryParamsHandling: 'merge',
        replaceUrl: true
      });
    });

    sub.add(() => this.activeGuards.delete(id));
    return sub;
  }

  /**
   * Returns the merged params owned by components whose route path
   * is a prefix of (or equal to) the given target path.
   * These components will remain alive after the navigation and
   * their params should be preserved in the URL.
   */
  getParamsToPreserve(targetPath: string): Record<string, string> {
    const result: Record<string, string> = {};
    this.activeGuards.forEach(({ getParams, routePath }) => {
      if (targetPath === routePath || targetPath.startsWith(routePath + '/')) {
        Object.assign(result, this.removeEmpty(getParams()));
      }
    });
    return result;
  }

  private removeEmpty(params: Record<string, string>): Record<string, string> {
    const result: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') {
        result[key] = value;
      }
    }
    return result;
  }
}
